<script setup lang="ts">
import { nextTick, onMounted } from 'vue';
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.js';
import {
    DisabledPropsInterface,
    useDisableComposable,
} from '@src/application/composables/disabled-composable.js';
import { Context } from '@src/application/types/context.js';
import { Size } from '@src/application/types/size.js';
import { TooltipProps } from '@components/tooltip-props.ts';
import Tooltip from '@components/tooltip.vue';

const props = withDefaults(defineProps<DisabledPropsInterface & {
    context?: Context,
    autofocus?: boolean,
    size?: Size,
    grow?: boolean,
    spread?: boolean,
    tooltip?: TooltipProps,
    content?: string,
    bold?: boolean,
}>(), {
    context: 'default',
    autofocus: false,
    size: '0',
});

const { singleRef: buttonRef, setSingleTemplateRef } = useSingleRefComposable<HTMLButtonElement>();

const { disabled } = useDisableComposable();

onMounted(async () => {
    if (props.autofocus) {
        await nextTick();
        if (buttonRef.value === null) {
            return;
        }
        buttonRef.value.focus();
    }
});
</script>

<template>
    <Tooltip v-if="tooltip !== undefined" v-bind="tooltip" :expansion="spread" align-content="grow">
        <button
            :ref="setSingleTemplateRef"
            type="button"
            :class="[
                $style.button,
                $style[context],
                $style['size_' + size],
                disabled ? $style.disabled : '',
                spread ? $style.spread : '',
                $style.grow,
                bold ? $style.bold : '',
            ]"
            :disabled="disabled"
        >
            <slot>
                {{ content }}
            </slot>
        </button>
    </Tooltip>
    <button
        v-else
        :ref="setSingleTemplateRef"
        type="button"
        :class="[
            $style.button,
            $style[context],
            $style['size_' + size],
            disabled ? $style.disabled : '',
            grow ? $style.grow : '',
            spread ? $style.spread : '',
            bold ? $style.bold : '',
        ]"
        :disabled="disabled"
    >
        <slot>
            {{ content }}
        </slot>
    </button>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@borders' as *;
@use '@backgrounds' as *;
@use '@sizes' as *;

$disabled-button-text: #a8a8a8;
$disabled-button-background: #e6e6e6;

.button {
    flex-shrink: 0; // Buttons sollen in Felx-Containern nicht schrumpfen
    line-height: 1.1;
    color: $default-text-color;
    text-decoration: none;
    cursor: pointer;
    border-style: solid;
    border-width: thin;
    border-radius: 2px;
    outline: 0;

    * {
        cursor: inherit;
        color: inherit;
    }
    &:hover * {
        color: inherit;
    }
    &.disabled,
    &.disabled:hover {
        color: $disabled-button-text;
        cursor: not-allowed;
        background-color: $disabled-button-background;
        border-color: $disabled-button-background;
    }
    &.size_0 {
        min-height: 2.2em;
        padding: .5em;
        font-size: $font-size-0;

        @media (min-width: 38em) {
            min-height: 2.5em;
            padding: .5em .8em;
        }
    }
    &.size_1 {
        min-height: 2.8em;
        padding: .8em;
        font-size: $font-size-1;
    }
    &.size_2 {
        min-height: 3em;
        padding: 1em;
        font-size: $font-size-2;
    }

    &.grow {
        height: 100%;
    }
    &.spread {
        width: 100%;
        flex-shrink: 1;
    }
    &.bold {
        font-weight: $weight-bold;
    }
}
.default {
    background-color: #fff;
    border-color: #8e9ba1;

    &:hover {
        color: $primary-text-color;
        border-color: $primary-line-color;
    }
    &.button:hover * {
        color: inherit;
    }
}
.primary {
    color: #fff;
    background-color: $primary-background;
    border-color: $primary-line-color;

    &:hover {
        background-color: $primary-background-hover;
        border-color: $primary-background-hover;
    }
}
.primary-outline {
    color: $primary-text-color;
    background-color: #fff;
    border-color: $primary-line-color;

    &:hover {
        background-color: $primary-background-hover;
        border-color: $primary-background-hover;
        color: #fff;
    }
}
.danger {
    color: #fff;
    background-color: $red-background;
    border-color: $red-background;

    &:hover {
        background-color: $red-background-hover;
        border-color: $red-background-hover;
    }
}
.warning {
    font-weight: $weight-medium;
    background-color: $yellow-background;
    border-color: $yellow-background;

    &:hover {
        background-color: $yellow-background-hover;
        border-color: $yellow-background-hover;
    }
}
</style>
